import { formatDate, formatNumber } from "@/lib/format";
import { BusinessRow } from "../types/webstoreAnalytics";
import { formatDuration } from "../utils/dateRanges";
import { TablePanel, Td, Th } from "./AnalyticsTable";

interface BusinessTableProps {
  businesses: BusinessRow[];
  selectedBusinessId: string;
  onSelectBusiness: (businessId: string) => void;
  limit?: number;
}

/**
 * Usage per merchant storefront. Clicking a row filters the whole tab to that
 * business; clicking the selected row clears the filter.
 *
 * A business with no name has never sent one — the name comes from the storefront's
 * business cookie, so an unnamed row is a store that was reached through a path that
 * did not set it. The id is shown instead of inventing a label.
 */
export function BusinessTable({
  businesses,
  selectedBusinessId,
  onSelectBusiness,
  limit = 15,
}: BusinessTableProps) {
  const shown = businesses.slice(0, limit);

  return (
    <TablePanel
      title="Businesses"
      subtitle="Click a row to filter the whole tab to one storefront"
      isEmpty={shown.length === 0}
      empty="No storefront had traffic in this range"
      footer={
        businesses.length > limit
          ? `Showing top ${limit} of ${businesses.length} businesses.`
          : undefined
      }
      head={
        <>
          <Th>Business</Th>
          <Th align="right">Sessions</Th>
          <Th align="right">Visitors</Th>
          <Th align="right">Page views</Th>
          <Th align="right">Avg. visit</Th>
          <Th align="right">Bounce</Th>
          <Th align="right">Last seen</Th>
        </>
      }
    >
      {shown.map((business) => {
        const isSelected = business.business_id === selectedBusinessId;
        return (
          <tr
            key={business.business_id}
            onClick={() => onSelectBusiness(isSelected ? "all" : business.business_id)}
            className={`cursor-pointer transition-colors ${
              isSelected ? "bg-blue-50/70" : "hover:bg-slate-50/60"
            }`}
          >
            <Td className="font-medium text-slate-800">
              <div>{business.business_name}</div>
              <div className="num-mono text-[10px] text-slate-400 mt-0.5">
                {business.business_id}
              </div>
            </Td>
            <Td align="right">
              {formatNumber(business.sessions)}
              <span className="text-slate-400 ml-1.5">{business.share_pct}%</span>
            </Td>
            <Td align="right">{formatNumber(business.unique_visitors)}</Td>
            <Td align="right">{formatNumber(business.page_views)}</Td>
            <Td align="right">{formatDuration(business.avg_duration_seconds)}</Td>
            <Td align="right">
              {business.bounce_rate_pct == null ? "—" : `${business.bounce_rate_pct}%`}
            </Td>
            <Td align="right">{formatDate(business.last_seen_at)}</Td>
          </tr>
        );
      })}
    </TablePanel>
  );
}
